import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Megaphone, Plus, ExternalLink, Upload, X, Image as ImageIcon, Loader2, Eye, Users, Activity, AlertCircle, Shield } from 'lucide-react';
import API_BASE_URL from '../config';

const AdCampaigns = () => {
  // Existing mock ads data
  const mockAds = [
    { id: '1', advertiser: 'Protein World Hyd', name: 'Summer Shred Promo', rate: 15.00, views: 1240, completions: 1040, status: 'Active' },
    { id: '2', advertiser: 'Cult Fit Gachibowli', name: 'Free Trial Pass Deal', rate: 12.50, views: 850, completions: 710, status: 'Paused' }
  ];

  // Ad Impressions Tracking
  const [searchParams, setSearchParams] = useSearchParams();
  const impressionsSectionRef = useRef(null);
  const [impressionsRange, setImpressionsRange] = useState('daily');
  const [impressionsData, setImpressionsData] = useState({ total: 0, unique: 0, impressions: [] });
  const [impressionsLoading, setImpressionsLoading] = useState(false);

  // Custom Banner States
  const [activeBannerUrl, setActiveBannerUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [feedback, setFeedback] = useState({ text: '', isError: false });

  // Leaderboard Monthly Sponsorship States
  const [sponsorshipInput, setSponsorshipInput] = useState('');
  const [updatingSponsorship, setUpdatingSponsorship] = useState(false);
  const [sponsorshipFeedback, setSponsorshipFeedback] = useState({ text: '', isError: false });

  // Streak Shield Ad URL States
  const [shieldAdUrlInput, setShieldAdUrlInput] = useState('');
  const [updatingShieldAdUrl, setUpdatingShieldAdUrl] = useState(false);
  const [shieldAdUrlFeedback, setShieldAdUrlFeedback] = useState({ text: '', isError: false });

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/settings/leaderboard-sponsorship`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.sponsorshipName) {
          setSponsorshipInput(data.sponsorshipName);
        }
      })
      .catch(err => console.error("Error fetching sponsorship name:", err));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    fetch(`${API_BASE_URL}/api/admin/settings/streak-shield-ad`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.videoUrl !== undefined) {
          setShieldAdUrlInput(data.videoUrl);
        }
      })
      .catch(err => console.error("Error fetching shield ad URL:", err));
  }, []);

  const handleUpdateSponsorship = async () => {
    setUpdatingSponsorship(true);
    setSponsorshipFeedback({ text: '', isError: false });
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE_URL}/api/admin/settings/leaderboard-sponsorship`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ sponsorshipName: sponsorshipInput })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSponsorshipFeedback({ text: 'Sponsorship name updated successfully!', isError: false });
      } else {
        setSponsorshipFeedback({ text: data.message || 'Failed to update sponsorship.', isError: true });
      }
    } catch (err) {
      setSponsorshipFeedback({ text: 'Network error. Failed to save sponsorship.', isError: true });
    } finally {
      setUpdatingSponsorship(false);
    }
  };

  const handleUpdateShieldAdUrl = async () => {
    setUpdatingShieldAdUrl(true);
    setShieldAdUrlFeedback({ text: '', isError: false });
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE_URL}/api/admin/settings/streak-shield-ad`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ videoUrl: shieldAdUrlInput })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setShieldAdUrlFeedback({ text: 'Streak Shield Ad URL updated successfully!', isError: false });
      } else {
        setShieldAdUrlFeedback({ text: data.message || 'Failed to update Streak Shield Ad URL.', isError: true });
      }
    } catch (err) {
      setShieldAdUrlFeedback({ text: 'Network error. Failed to save Streak Shield Ad URL.', isError: true });
    } finally {
      setUpdatingShieldAdUrl(false);
    }
  };

  // Sync range and view from URL query params
  useEffect(() => {
    const rangeParam = searchParams.get('range');
    if (rangeParam && ['daily', 'weekly', 'monthly'].includes(rangeParam)) {
      setImpressionsRange(rangeParam);
    }
    const viewParam = searchParams.get('view');
    if (viewParam === 'impressions') {
      setTimeout(() => {
        impressionsSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, [searchParams]);

  const fetchImpressions = async (range) => {
    setImpressionsLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE_URL}/api/admin/analytics/ad-impressions?range=${range}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setImpressionsData({
            total: data.total,
            unique: data.unique,
            impressions: data.impressions || []
          });
        }
      }
    } catch (err) {
      console.error("Failed to fetch ad impressions:", err);
    } finally {
      setImpressionsLoading(false);
    }
  };

  useEffect(() => {
    fetchImpressions(impressionsRange);
  }, [impressionsRange]);

  const handleRangeChange = (newRange) => {
    setImpressionsRange(newRange);
    setSearchParams({ view: 'impressions', range: newRange });
  };

  // Fetch the currently active banner URL on mount
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/settings/fitscan-ad`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.imageUrl) {
          setActiveBannerUrl(data.imageUrl);
        }
      })
      .catch(err => console.error("Error fetching ad banner:", err));
  }, []);

  // Handle local file selection and validation
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setFeedback({ text: 'Please select a valid image file (PNG, JPEG, WebP).', isError: true });
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      setFeedback({ text: 'Image size exceeds the 2MB limit.', isError: true });
      return;
    }

    // Clean up any previously created object URL to prevent memory leaks
    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
    }

    setSelectedFile(file);
    setLocalPreviewUrl(URL.createObjectURL(file));
    setFeedback({ text: '', isError: false });
  };

  // Perform upload to the backend
  const handleUploadBanner = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setFeedback({ text: '', isError: false });

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE_URL}/api/admin/settings/fitscan-ad`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await res.json();
      if (res.ok && data.success) {
        // Clean up preview object URL
        if (localPreviewUrl) {
          URL.revokeObjectURL(localPreviewUrl);
        }
        setActiveBannerUrl(data.imageUrl);
        setSelectedFile(null);
        setLocalPreviewUrl(null);
        setFeedback({ text: 'Dashboard banner updated successfully!', isError: false });
      } else {
        setFeedback({ text: data.message || 'Failed to upload image.', isError: true });
      }
    } catch (err) {
      setFeedback({ text: 'Network error. Failed to customize banner.', isError: true });
    } finally {
      setUploading(false);
    }
  };

  // Cancel file selection and clean up local preview URL
  const handleCancelSelection = () => {
    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
    }
    setSelectedFile(null);
    setLocalPreviewUrl(null);
    setFeedback({ text: '', isError: false });
  };

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Ad Campaigns & Banners</h1>
          <p className="text-gray-400 text-sm mt-1">Configure promotions and customize the default dashboard ad banner.</p>
        </div>
        <button className="flex items-center px-4 py-2.5 bg-[#6CD800] hover:bg-[#5bbf00] text-[#0B0F19] font-bold rounded-lg text-sm transition-colors cursor-not-allowed opacity-60">
          <Plus size={16} className="mr-1.5" /> Create Campaign
        </button>
      </div>

      {/* Global Dashboard Ad Banner Customizer */}
      <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl shadow-lg p-6 space-y-6">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <ImageIcon size={20} className="text-[#6CD800]" />
            Global Dashboard Ad Banner
          </h2>
          <p className="text-gray-400 text-xs mt-1">
            This image is displayed at the bottom of the QR code scan tab on all user/member dashboards (recommended size: 1280x720, 16:9 aspect ratio).
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          {/* Active Banner Display */}
          <div className="space-y-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 block">Current Active Banner</span>
            <div className="aspect-video rounded-xl overflow-hidden border border-[#1E293B] bg-[#0B0F19] flex items-center justify-center relative group">
              {activeBannerUrl ? (
                <img
                  src={activeBannerUrl}
                  alt="Current Dashboard Banner"
                  className="w-full h-full object-cover transition-opacity duration-300"
                />
              ) : (
                <div className="text-gray-500 text-sm flex flex-col items-center">
                  <ImageIcon size={32} className="mb-2 text-gray-600 animate-pulse" />
                  Loading active banner...
                </div>
              )}
            </div>
          </div>

          {/* Upload / Selector Section */}
          <div className="space-y-4">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 block">Upload New Banner</span>

            {!localPreviewUrl ? (
              <label className="flex flex-col items-center justify-center h-40 border-2 border-dashed border-[#1E293B] hover:border-[#6CD800]/50 rounded-xl cursor-pointer bg-[#0B0F19]/40 transition-colors p-4 group">
                <Upload size={28} className="text-gray-500 group-hover:text-[#6CD800] mb-2 transition-colors" />
                <span className="text-xs font-semibold text-gray-300 group-hover:text-white">Click to upload image</span>
                <span className="text-[10px] text-gray-500 mt-1">PNG, JPG or WebP (max 2MB)</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>
            ) : (
              <div className="space-y-4">
                {/* Local Preview of Selected File */}
                <div className="relative aspect-video rounded-xl overflow-hidden border border-[#6CD800]/40 bg-[#0B0F19]">
                  <img
                    src={localPreviewUrl}
                    alt="New Banner Preview"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 right-2 flex gap-2">
                    <button
                      onClick={handleCancelSelection}
                      disabled={uploading}
                      className="bg-black/70 hover:bg-black text-white p-1.5 rounded-full border border-gray-700 transition-colors"
                      title="Cancel"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-black/70 text-[#6CD800] px-2.5 py-0.5 rounded text-[10px] font-mono border border-[#6CD800]/20">
                    PREVIEW
                  </div>
                </div>

                {/* 2-Step Confirmation Actions */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleUploadBanner}
                    disabled={uploading}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#6CD800] hover:bg-[#5bbf00] text-[#0B0F19] font-bold rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Saving to Cloud...
                      </>
                    ) : (
                      'Confirm & Upload Banner'
                    )}
                  </button>
                  <button
                    onClick={handleCancelSelection}
                    disabled={uploading}
                    className="px-4 py-2.5 bg-[#1E293B] border border-[#334155] text-gray-300 font-semibold rounded-lg text-sm hover:border-gray-500 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Notification Feedback Message */}
            {feedback.text && (
              <div className={`p-3 rounded-lg text-xs font-medium border ${feedback.isError
                  ? 'bg-red-500/10 border-red-500/25 text-red-400'
                  : 'bg-green-500/10 border-green-500/25 text-[#6CD800]'
                }`}>
                {feedback.text}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Leaderboard Sponsorship Settings */}
      <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl shadow-lg p-6 space-y-6">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Megaphone size={20} className="text-[#6CD800]" />
            Leaderboard Monthly Sponsorship
          </h2>
          <p className="text-gray-400 text-xs mt-1">
            Update the sponsorship name displayed at the top of the Gym Leaderboard tab on member dashboards.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 max-w-xl">
          <input
            type="text"
            value={sponsorshipInput}
            onChange={(e) => setSponsorshipInput(e.target.value)}
            className="w-full px-4 py-2.5 bg-[#0B0F19] border border-[#1E293B] rounded-lg text-white text-sm focus:outline-none focus:border-[#6CD800] transition-colors"
            placeholder="Enter sponsor name (e.g. X Shop)"
          />
          <button
            onClick={handleUpdateSponsorship}
            disabled={updatingSponsorship}
            className="w-full sm:w-auto px-6 py-2.5 bg-[#6CD800] hover:bg-[#5bbf00] text-[#0B0F19] font-bold rounded-lg text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {updatingSponsorship ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Saving...
              </>
            ) : (
              'Save Sponsor'
            )}
          </button>
        </div>

        {sponsorshipFeedback.text && (
          <div className={`p-3 rounded-lg text-xs font-medium border max-w-xl ${sponsorshipFeedback.isError
              ? 'bg-red-500/10 border-red-500/25 text-red-400'
              : 'bg-green-500/10 border-green-500/25 text-[#6CD800]'
            }`}>
            {sponsorshipFeedback.text}
          </div>
        )}
      </div>

      {/* Streak Shield Settings */}
      <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl shadow-lg p-6 space-y-6">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Shield size={20} className="text-[#6CD800]" />
            Streak Shield Ad URL
          </h2>
          <p className="text-gray-400 text-xs mt-1">
            Direct link to a 10-second MP4 video. Leave blank to show the simulated ad countdown.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 max-w-xl">
          <input
            type="text"
            value={shieldAdUrlInput}
            onChange={(e) => setShieldAdUrlInput(e.target.value)}
            className="w-full px-4 py-2.5 bg-[#0B0F19] border border-[#1E293B] rounded-lg text-white text-sm focus:outline-none focus:border-[#6CD800] transition-colors"
            placeholder="https://cdn.example.com/ads/streak-shield-ad.mp4"
          />
          <button
            onClick={handleUpdateShieldAdUrl}
            disabled={updatingShieldAdUrl}
            className="w-full sm:w-auto px-6 py-2.5 bg-[#6CD800] hover:bg-[#5bbf00] text-[#0B0F19] font-bold rounded-lg text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {updatingShieldAdUrl ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Saving...
              </>
            ) : (
              'Save URL'
            )}
          </button>
        </div>

        {shieldAdUrlFeedback.text && (
          <div className={`p-3 rounded-lg text-xs font-medium border max-w-xl ${shieldAdUrlFeedback.isError
              ? 'bg-red-500/10 border-red-500/25 text-red-400'
              : 'bg-green-500/10 border-green-500/25 text-[#6CD800]'
            }`}>
            {shieldAdUrlFeedback.text}
          </div>
        )}
      </div>

      {/* Campaigns Listing */}
      <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl shadow-lg overflow-hidden">
        <div className="p-6 border-b border-[#1E293B]">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Megaphone size={20} className="text-[#6CD800]" />
            Active Ad Campaigns
          </h2>
          <p className="text-gray-400 text-xs mt-1">Configure Cost Per View (CPV) visual promotions displayed on challenges.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-300">
            <thead className="text-xs text-gray-400 uppercase bg-[#0B0F19] border-b border-[#1E293B]">
              <tr>
                <th className="px-6 py-4">Campaign Name</th>
                <th className="px-6 py-4">Advertiser</th>
                <th className="px-6 py-4 text-center">CPV Rate (₹)</th>
                <th className="px-6 py-4 text-center">Views</th>
                <th className="px-6 py-4 text-center">Completions</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1E293B]">
              {mockAds.map(ad => (
                <tr key={ad.id} className="hover:bg-[#1E293B]/20">
                  <td className="px-6 py-4 font-semibold text-white">{ad.name}</td>
                  <td className="px-6 py-4 text-gray-400">{ad.advertiser}</td>
                  <td className="px-6 py-4 text-center font-mono">₹{ad.rate.toFixed(2)}</td>
                  <td className="px-6 py-4 text-center">{ad.views}</td>
                  <td className="px-6 py-4 text-center">{ad.completions}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${ad.status === 'Active' ? 'bg-[#6CD800]/10 text-[#6CD800] border border-[#6CD800]/20' : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'
                      }`}>{ad.status}</span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button className="text-[#6CD800] hover:underline text-xs flex items-center ml-auto cursor-not-allowed opacity-60">
                      Edit <ExternalLink size={12} className="ml-1" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ad Impression Logs */}
      <div
        ref={impressionsSectionRef}
        className="bg-[#0F172A] border border-[#1E293B] rounded-xl shadow-lg overflow-hidden transition-all duration-300"
      >
        {/* Header */}
        <div className="p-6 border-b border-[#1E293B] flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-[#0B0F19]/40">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Eye size={20} className="text-[#6CD800]" />
              Ad Impression Logs
            </h2>
            <p className="text-gray-400 text-xs mt-1">
              Monitor ad views and user taps platform-wide.
            </p>
          </div>

          {/* Daily/Weekly/Monthly range filter */}
          <div className="flex items-center gap-1.5 bg-[#0B0F19] border border-[#1E293B] p-1 rounded-lg">
            {[
              { id: 'daily', label: 'Daily' },
              { id: 'weekly', label: 'Weekly' },
              { id: 'monthly', label: 'Monthly' }
            ].map(btn => (
              <button
                key={btn.id}
                onClick={() => handleRangeChange(btn.id)}
                className={`px-3.5 py-1.5 rounded-md text-xs font-semibold transition-all ${impressionsRange === btn.id
                    ? 'bg-[#6CD800] text-[#0B0F19] font-bold shadow-md'
                    : 'text-gray-400 hover:text-white'
                  }`}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>

        {/* Stats Summary & Warning Notice */}
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Total Impressions Card */}
            <div className="bg-[#0B0F19] border border-[#1E293B] p-4 rounded-xl flex items-center gap-4">
              <div className="p-2.5 bg-[#6CD800]/10 rounded-lg text-[#6CD800]">
                <Activity size={20} />
              </div>
              <div>
                <span className="text-gray-400 text-[11px] font-semibold uppercase tracking-wider block">Total Impressions</span>
                <span className="text-2xl font-bold text-white">{impressionsData.total}</span>
              </div>
            </div>

            {/* Unique Users Card */}
            <div className="bg-[#0B0F19] border border-[#1E293B] p-4 rounded-xl flex items-center gap-4">
              <div className="p-2.5 bg-blue-500/10 rounded-lg text-blue-400">
                <Users size={20} />
              </div>
              <div>
                <span className="text-gray-400 text-[11px] font-semibold uppercase tracking-wider block">Unique Users</span>
                <span className="text-2xl font-bold text-white">{impressionsData.unique}</span>
              </div>
            </div>
          </div>

          {/* Row limit warning notice */}
          {impressionsData.impressions.length >= 500 && (
            <div className="flex items-center gap-2 px-4 py-3 bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-xs rounded-lg">
              <AlertCircle size={15} className="flex-shrink-0" />
              <span>Showing the latest 500 impressions due to performance limits.</span>
            </div>
          )}

          {/* Table list */}
          <div className="border border-[#1E293B] rounded-xl overflow-hidden bg-[#0B0F19]/20">
            <div className="max-h-[400px] overflow-y-auto pr-1">
              <table className="w-full text-left text-sm text-gray-300">
                <thead className="text-xs text-gray-400 uppercase bg-[#0B0F19] border-b border-[#1E293B] sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3.5">User</th>
                    <th className="px-6 py-3.5">Gym</th>
                    <th className="px-6 py-3.5">Timestamp</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1E293B]">
                  {impressionsLoading ? (
                    <tr>
                      <td colSpan="3" className="text-center py-10 text-gray-500">
                        <div className="flex items-center justify-center gap-2">
                          <Loader2 size={16} className="animate-spin text-[#6CD800]" />
                          Loading logs...
                        </div>
                      </td>
                    </tr>
                  ) : impressionsData.impressions.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="text-center py-10 text-gray-500">
                        No ad impressions recorded for this period.
                      </td>
                    </tr>
                  ) : (
                    impressionsData.impressions.map((imp, idx) => {
                      const time = new Date(imp.impressionTime);
                      const timeStr = time.toLocaleString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      });
                      return (
                        <tr key={idx} className="hover:bg-[#1E293B]/20 transition-colors">
                          <td className="px-6 py-3.5">
                            <div className="flex items-center gap-2">
                              <div className="w-6.5 h-6.5 rounded-full bg-[#6CD800]/20 flex items-center justify-center text-[10px] text-[#6CD800] font-bold">
                                {imp.name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div className="flex flex-col flex-wrap">
                                <span className="text-white font-medium text-xs">{imp.name || 'Unknown User'}</span>
                                <span className="text-gray-500 text-[10px] font-mono">@{imp.username}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-3.5">
                            <div className="flex flex-col">
                              <span className="text-gray-300 text-xs font-semibold">{imp.gymName}</span>
                              <span className="text-gray-500 text-[10px] font-mono">{imp.gymCode}</span>
                            </div>
                          </td>
                          <td className="px-6 py-3.5 text-xs text-gray-400">
                            {timeStr}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdCampaigns;
